import React, { useContext, useState } from "react";
import {
    Button,
    Form, FormFeedback,
    FormGroup,
    Input,
    Label,
    Modal,
    ModalBody,
    ModalHeader
} from "reactstrap";
import Select from "react-select";
import {useMutation, useQuery, useQueryClient} from "@tanstack/react-query";
import { createCustomerShippingAddress, getCountry, getState } from "../../api/api";
import { toast } from "react-toastify";
import AuthContext from "../../context/AuthContext";
import handleUnauthorized401QueryError from "../../utils/handleUnauthorized401QueryError";
import handleUnauthorized401MutateError from "../../utils/handleUnauthorized401MutateError";
import Loader from "../../components/Loader";

function AddCustomerShippingAddressModal({ isOpen, toggle, email }) {
    const { refreshToken } = useContext(AuthContext);

    const [label, setLabel] = useState("");
    const [addressLine1, setAddressLine1] = useState("");
    const [addressLine2, setAddressLine2] = useState("");
    const [addressLine3, setAddressLine3] = useState("");
    const [city, setCity] = useState("");
    const [pincode, setPincode] = useState("");
    const [isPrimaryAddress, setIsPrimaryAddress] = useState(false);

    const [country, setCountry] = useState(null);
    const [stateObj, setStateObj] = useState(null);
    const [countryOptions, setCountryOptions] = useState([]);
    const [stateOptions, setStateOptions] = useState([]);

    const queryClient = useQueryClient();

    // Countries
    useQuery(
        ["shipping-country"],
        () => getCountry(1, "", "", "", "", 100),
        {
            enabled: isOpen,
            onSuccess: (data) => {
                setCountryOptions(
                    data.results.map((c) => ({
                        value: c.name,
                        label: c.name
                    }))
                );
            },
            onError: (error) =>
                handleUnauthorized401QueryError(error, refreshToken)
        }
    );

    // States
    useQuery(
        ["shipping-state", country?.value],
        () => getState(1, "", "", country?.value, "", "", 2000),
        {
            enabled: !!country,
            onSuccess: (data) => {
                setStateOptions(
                    data.results.map((s) => ({
                        value: s.name,
                        label: s.name
                    }))
                );
            },
            onError: (error) =>
                handleUnauthorized401QueryError(error, refreshToken)
        }
    );

    const addCustomerShippingAddressMutation = useMutation({
        mutationFn: createCustomerShippingAddress,
        onSuccess: () => {
            toast.success("Shipping address added");
            toggle();
            queryClient.invalidateQueries(['shippingAddress', email]);
            setLabel('');
            setAddressLine1('')
            setAddressLine2('')
            setAddressLine3('')
            setCity('')
            setPincode('')
            setIsPrimaryAddress(false)
            setCountry(null)
            setStateObj(null)
        },
        onError: (err, variables) =>{
            handleUnauthorized401MutateError(
                err,
                refreshToken,
                addCustomerShippingAddressMutation,
                variables
            )
        }
    });

    const handleSubmit = (e) => {
        e.preventDefault();
        const formData = new FormData();
        formData.append("customer", email);
        formData.append("label", label);
        formData.append("address_line1", addressLine1);
        formData.append("address_line2", addressLine2 || "");
        formData.append("address_line3", addressLine3 || "");
        formData.append("country", country?.value || "");
        formData.append("state", stateObj?.value || "");
        formData.append("city", city);
        formData.append("pincode", pincode);
        formData.append("is_primary", isPrimaryAddress);

        addCustomerShippingAddressMutation.mutate({ formData });
    };

    return (
        <Modal isOpen={isOpen} toggle={toggle}>
            <ModalHeader toggle={toggle} close={
                <button className="close" onClick={toggle}>
                    &times;
                </button>
            }>
                Add Shipping Address
            </ModalHeader>

            <ModalBody>
                <Form onSubmit={handleSubmit}>
                    <FormGroup>
                        <Label>Label</Label>
                        <Input
                            value={label}
                            onChange={(e) => setLabel(e.target.value)}
                            invalid={
                                addCustomerShippingAddressMutation.isError &&
                                addCustomerShippingAddressMutation.error.response.data.label
                            }
                        />
                        {addCustomerShippingAddressMutation.isError && addCustomerShippingAddressMutation.error.response.data.label && (
                            <FormFeedback invalid>
                                {addCustomerShippingAddressMutation.error.response.data.label}
                            </FormFeedback>
                        )}
                    </FormGroup>

                    <FormGroup>
                        <Label>Address Line 1</Label>
                        <Input
                            value={addressLine1}
                            onChange={(e) => setAddressLine1(e.target.value)}
                            invalid={
                                addCustomerShippingAddressMutation.isError &&
                                addCustomerShippingAddressMutation.error.response.data.address_line1
                            }
                        />
                        {addCustomerShippingAddressMutation.isError && addCustomerShippingAddressMutation.error.response.data.address_line1 && (
                            <FormFeedback invalid>
                                {addCustomerShippingAddressMutation.error.response.data.address_line1}
                            </FormFeedback>
                        )}
                    </FormGroup>

                    <FormGroup>
                        <Label>Address Line 2</Label>
                        <Input
                            value={addressLine2}
                            onChange={(e) => setAddressLine2(e.target.value)}
                            invalid={
                                addCustomerShippingAddressMutation.isError &&
                                addCustomerShippingAddressMutation.error.response.data.address_line2
                            }
                        />
                        {addCustomerShippingAddressMutation.isError && addCustomerShippingAddressMutation.error.response.data.address_line2 && (
                            <FormFeedback invalid>
                                {addCustomerShippingAddressMutation.error.response.data.address_line2}
                            </FormFeedback>
                        )}
                    </FormGroup>

                    <FormGroup>
                        <Label>Address Line 3</Label>
                        <Input
                            value={addressLine3}
                            onChange={(e) => setAddressLine3(e.target.value)}
                            invalid={
                                addCustomerShippingAddressMutation.isError &&
                                addCustomerShippingAddressMutation.error.response.data.address_line3
                            }
                        />
                        {addCustomerShippingAddressMutation.isError && addCustomerShippingAddressMutation.error.response.data.address_line3 && (
                            <FormFeedback invalid>
                                {addCustomerShippingAddressMutation.error.response.data.address_line3}
                            </FormFeedback>
                        )}
                    </FormGroup>

                    <FormGroup>
                        <Label>Country</Label>
                        <Select
                            value={country}
                            options={countryOptions}
                            placeholder="Select country"
                            onChange={(opt) => {
                                setCountry(opt);
                                setStateObj(null);
                                setStateOptions([]);
                            }}
                            styles={{
                                control: (baseStyles) => (
                                    {
                                        ...baseStyles,
                                        borderColor: "#495561",
                                        backgroundColor: "#414d59",
                                        color: "white",
                                        padding: "0.45 0.9",
                                        border: 'none',
                                        boxShadow: 'none'
                                    }
                                ),
                                menu: (baseStyles) => (
                                    {
                                        ...baseStyles,
                                        backgroundColor: "#414d59",
                                        color: "#a8b5c3"
                                    }
                                ),
                                input: (baseStyles) => (
                                    {
                                        ...baseStyles,
                                        color: "white"
                                    }
                                ),
                                singleValue: (baseStyles) => ({
                                    ...baseStyles,
                                    color: "white"
                                }),
                            }}
                        />
                    </FormGroup>

                    <FormGroup>
                        <Label>State</Label>
                        <Select
                            value={stateObj}
                            options={stateOptions}
                            placeholder="Select state"
                            onChange={setStateObj}
                            isDisabled={!country}
                            styles={{
                                control: (baseStyles) => (
                                    {
                                        ...baseStyles,
                                        borderColor: "#495561",
                                        backgroundColor: "#414d59",
                                        color: "white",
                                        padding: "0.45 0.9",
                                        border: 'none',
                                        boxShadow: 'none'
                                    }
                                ),
                                menu: (baseStyles) => (
                                    {
                                        ...baseStyles,
                                        backgroundColor: "#414d59",
                                        color: "#a8b5c3"
                                    }
                                ),
                                input: (baseStyles) => (
                                    {
                                        ...baseStyles,
                                        color: "white"
                                    }
                                ),
                                singleValue: (baseStyles) => ({
                                    ...baseStyles,
                                    color: "white"
                                }),
                            }}
                        />
                    </FormGroup>

                    <FormGroup>
                        <Label>City</Label>
                        <Input
                            value={city}
                            onChange={(e) => setCity(e.target.value)}
                            invalid={
                                addCustomerShippingAddressMutation.isError &&
                                addCustomerShippingAddressMutation.error.response.data.city
                            }
                        />
                        {addCustomerShippingAddressMutation.isError && addCustomerShippingAddressMutation.error.response.data.city && (
                            <FormFeedback invalid>
                                {addCustomerShippingAddressMutation.error.response.data.city}
                            </FormFeedback>
                        )}
                    </FormGroup>

                    <FormGroup>
                        <Label>Pincode</Label>
                        <Input
                            value={pincode}
                            onChange={(e) => setPincode(e.target.value)}
                            invalid={
                                addCustomerShippingAddressMutation.isError &&
                                addCustomerShippingAddressMutation.error.response.data.pincode
                            }
                        />
                        {addCustomerShippingAddressMutation.isError && addCustomerShippingAddressMutation.error.response.data.pincode && (
                            <FormFeedback invalid>
                                {addCustomerShippingAddressMutation.error.response.data.pincode}
                            </FormFeedback>
                        )}
                    </FormGroup>

                    <FormGroup check>
                        <Input
                            type="checkbox"
                            checked={isPrimaryAddress}
                            onChange={() => setIsPrimaryAddress(!isPrimaryAddress)}
                            invalid={
                                addCustomerShippingAddressMutation.isError &&
                                addCustomerShippingAddressMutation.error.response.data.is_primary
                            }
                        />
                        <Label check>Set as Primary Address</Label>
                        {addCustomerShippingAddressMutation.isError && addCustomerShippingAddressMutation.error.response.data.is_primary && (
                            <FormFeedback invalid>
                                {addCustomerShippingAddressMutation.error.response.data.is_primary}
                            </FormFeedback>
                        )}
                    </FormGroup>

                    {/*<Button*/}
                    {/*    color="primary"*/}
                    {/*    block*/}
                    {/*    disabled={addCustomerShippingAddressMutation.isLoading}*/}
                    {/*>*/}
                    {/*    {addCustomerShippingAddressMutation.isLoading*/}
                    {/*        ? <><Loader size="small" /> Adding New Shipping Address...</>*/}
                    {/*        : "Add New Shipping Address"}*/}
                    {/*</Button>*/}

                    <Button
                        color="#4d5bed"
                        className="bx-btn-primary mt-2"
                        block
                        disabled={addCustomerShippingAddressMutation.isLoading}
                    >
                        {addCustomerShippingAddressMutation.isLoading? <><Loader size={'small'}/> Adding New Shipping Address...</>: `Add New Shipping Address`}

                    </Button>
                </Form>
            </ModalBody>
        </Modal>
    );
}

export default AddCustomerShippingAddressModal;
